<script setup>
import { computed, ref, watchEffect, reactive } from "vue";
import Swal from "sweetalert2";
import sendIcon from "@/assets/img/send.png";
import CalendarIcon from "@/assets/svg/calendar-icon.svg?component";
import SendCommentIcon from "@/assets/svg/send-comment-icon.svg?component";
import { useValidation } from "@composables/useValidation";
import { required, maxLength } from "@services/i18n-validators";
import { ElNotification } from "element-plus";
import DeleteIcon from "@/assets/svg/delete-icon.svg?component";
import EditIcon from "@/assets/svg/edit-icon.svg?component";
import { useUserStore } from '@store/user.js'
import upDotsIcon from "@/assets/img/up-dots.png";
const userStore = useUserStore()


const emit = defineEmits(["reply", "resolve", "goto", "editComment", "deleteComment"]);
const props = defineProps({
    comment: { type: Object, required: true },
    replies: { type: Array, default: () => [] },
    showActions: { type: Boolean, default: false },
    showPageMeta: { type: Boolean, default: false },
    replyPlaceholder: { type: String, default: "Add Reply" },
    isClosed: { type: Boolean, default: false },
    canTakeAction: { type: Boolean, default: true },
});

const form = reactive({ reply: "" });
const localReplies = ref([]);
const isSubmitting = ref(false);
const deleteModal = ref([false]);
const deleteItem = ref({ type: '', id: null });
const popoverVisible = ref(false);
const replyPopovers = ref({});
const editingComment = ref(null);
const editingReply = ref(null);
const editText = ref('');
const editInputRef = ref(null);
const replyEditInputRefs = ref({});

const { v$ } = useValidation(
    reactive({ reply: { required, maxLength: maxLength(1000) } }),
    form
);

watchEffect(() => {
    const list = Array.isArray(props.replies)
        ? props.replies
        : props.comment?.replies || [];
    localReplies.value = list
        .slice()
        .sort(
            (a, b) =>
                new Date(b?.created_at || b?.ts || 0) -
                new Date(a?.created_at || a?.ts || 0)
        );
});

const authorName = computed(() => props.comment?.user?.name || "Anonymous");
const formattedDate = computed(() => formatDate(props.comment?.created_at));
const avatarUrl = computed(() => props.comment?.user?.avatar?.url || null);
const initials = computed(() =>
    (authorName.value || "U").slice(0, 1).toUpperCase()
);

function submitReply() {
    if (!v$.value.$validate()) {
        ElNotification({
            title: "Validation",
            message: "Please enter a reply (max 1000 characters).",
            type: "warning",
            position: "bottom-left",
        });
        return;
    }
    const text = (form.reply || "").trim();
    if (!text) return;
    if (text.length > 1000) {
        return;
    }
    isSubmitting.value = true;
    const done = () => {
        isSubmitting.value = false;
    };
    // Emit both keys for backward/forward compatibility with backend handlers
    emit("reply", { reply: text, text, comment: props.comment, done });
    form.reply = "";
    if (v$ && v$.value) v$.value.$reset();
}

function formatDate(d) {
    if (!d) return "";
    try {
        const dt = new Date(d);
        return (
            dt.toLocaleString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
            }) +
            ", " +
            dt.toLocaleDateString()
        );
    } catch {
        return "";
    }
}

async function confirmResolve() {
    const res = await Swal.fire({
        title: "Resolve this comment?",
        text: "This will mark the comment as resolved and hide it from the list.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, resolve",
        cancelButtonText: "Cancel",
    });
    if (res.isConfirmed) {
        emit("resolve", { comment: props.comment });
    }
}

function getReplyName(r) {
    const raw = r && (r.user?.name ?? r.user);
    return typeof raw === "string" && raw.trim()
        ? raw
        : r?.user?.name || "User";
}

function getReplyInitial(r) {
    const name = getReplyName(r);
    const initial = name && typeof name === "string" ? name.charAt(0) : "U";
    return initial.toUpperCase();
}

// Render context with highlighted mentions similar to Slack
const renderedContext = computed(() => {
    const raw = (props.comment?.context || "").toString();
    const mentions = Array.isArray(props.comment?.mentions)
        ? props.comment.mentions
        : [];
    if (!raw) return "";
    // Basic escape to prevent HTML injection
    const escapeHtml = (s) =>
        s
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    let text = escapeHtml(raw);
    // Highlight tokens that start with @ and match any mention name
    if (mentions.length > 0) {
        const names = mentions
            .filter((n) => typeof n === "string" && n.trim().length)
            .map((n) => n.trim());
        // Sort by length desc to avoid partial overlaps
        names.sort((a, b) => b.length - a.length);
        for (const name of names) {
            // Match @Name where Name is the exact user name, boundaries to avoid mid-word
            const pattern = new RegExp(
                `(^|\\s)@${name.replace(
                    /[-/\\^$*+?.()|[\]{}]/g,
                    "\\$&"
                )}(?=$|[\n\r\t .,!?;:])`,
                "g"
            );
            text = text.replace(
                pattern,
                (m, p1) =>
                    `${p1}<span style=\"color: #1D4ED8; font-weight: 700;\">@${name}</span>`
            );
        }
    } else {
        // Fallback: style any @word token
        text = text.replace(
            /(^|\s)@([\p{L}0-9_.-]+)/gu,
            '$1<span style="color: #1D4ED8; font-weight: 700;">@$2</span>'
        );
    }
    return text;
});

function deleteComment(type, id) {
    deleteItem.value = { type, id };
    deleteModal.value[0] = true;
}

function confirmDelete() {
    deleteModal.value[0] = false;
    try {
        emit("deleteComment", deleteItem.value);
        ElNotification({
            title: 'Success',
            message: `${deleteItem.value.type === 'comment' ? 'Comment' : 'Reply'} deleted successfully`,
            type: 'success',
            position: 'bottom-left',
        });
    } catch (error) {
        console.error('Error deleting comment:', error);
        ElNotification({
            title: 'Error',
            message: 'Failed to delete comment',
            type: 'error',
            position: 'bottom-left',
        });
    }
}

function handleEdit() {
    popoverVisible.value = false;
    editingComment.value = props.comment.id;
    editText.value = props.comment.context || '';
    // Focus input after DOM update
    setTimeout(() => {
        if (editInputRef.value) {
            editInputRef.value.focus();
        }
    }, 100);
}

function handleReplyEdit(replyId) {
    replyPopovers.value[replyId] = false;
    editingReply.value = replyId;
    const reply = localReplies.value.find(r => r.id === replyId);
    if (reply) {
        editText.value = reply.reply || reply.text || '';
    }
    // Focus input after DOM update
    setTimeout(() => {
        if (replyEditInputRefs.value[replyId]) {
            replyEditInputRefs.value[replyId].focus();
        }
    }, 100);
}

function handleDelete() {
    popoverVisible.value = false;
    deleteComment('comment', props.comment.id);
}

function handleReplyDelete(replyId) {
    replyPopovers.value[replyId] = false;
    deleteComment('reply', replyId);
}

function saveEdit() {
    if (editingComment.value) {
        emit("editComment", { 
            type: 'comment',
            id: props.comment.id,
            text: editText.value 
        });
        editingComment.value = null;
        editText.value = '';
    }
}

function saveReplyEdit(replyId) {
    const reply = localReplies.value.find(r => r.id === replyId);
    if (reply) {
        emit("editComment", { 
            type: 'reply',
            id: replyId,
            text: editText.value
        });
        editingReply.value = null;
        editText.value = '';
    }
}

function cancelEdit() {
    editingComment.value = null;
    editText.value = '';
}

function cancelReplyEdit() {
    editingReply.value = null;
    editText.value = '';
}

function handleKeydown(event, type, id = null) {
    if (event.key === 'Escape') {
        if (type === 'comment') {
            cancelEdit();
        } else if (type === 'reply') {
            cancelReplyEdit();
        }
    } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (type === 'comment') {
            saveEdit();
        } else if (type === 'reply') {
            saveReplyEdit(id);
        }
    }
}

function handleBlur(type, id = null) {
    // Use setTimeout to allow click events to fire first
    setTimeout(() => {
        if (type === 'comment' && editingComment.value) {
            saveEdit();
        } else if (type === 'reply' && editingReply.value === id) {
            saveReplyEdit(id);
        }
    }, 200);
}
</script>

<template>
    <div class="comment-card" :class="{ closed: isClosed }">
        <div class="card-header">
            <div class="user-info">
                <img
                    v-if="avatarUrl"
                    :src="avatarUrl"
                    class="user-avatar-img"
                    alt="avatar"
                />
                <div v-else class="user-avatar">{{ initials }}</div>
                <div class="user-details">
                    <strong class="user-name">{{ authorName }}</strong>
                    <small class="timestamp">
                        <CalendarIcon />
                        {{ formattedDate }}
                    </small>
                </div>
            </div>
            <div class="actions-right" v-if="!isClosed && canTakeAction">
                
                <button
                    class="resolve-btn"
                    @click="confirmResolve"
                    title="Resolve"
                >
                    Mark As Resolved
                    <img
                        width="20"
                        src="@/assets/img/checkmark-square-02.png"
                        alt=""
                    />
                </button>
                <el-popover
                    v-if="!isClosed"
                    placement="bottom-end"
                    :offset="4"
                    trigger="click"
                    popper-class="comment-popover"
                    :append-to-body="false"
                    v-model:visible="popoverVisible"
                >
                    <template #reference>
                        <el-button
                            size="small"
                            type="primary"
                            style="background: none; border: none; padding: 0;"
                        >
                            <img
                                :src="upDotsIcon"
                                style="width: 22px; height: 22px; cursor: pointer;"
                                alt=""
                            />
                        </el-button>
                    </template>
                    <ul class="list-unstyled dropdown-menu-end mb-0">
                        <li>
                            <button
                                class="dropdown-item d-flex align-items-center gap-2 px-2"
                                @click.prevent="handleEdit"
                            >
                                <EditIcon />
                                <span>Edit</span>
                            </button>
                        </li>
                        <li>
                            <button
                                v-if="userStore.user.roles.includes('Super Admin')"
                                class="dropdown-item d-flex align-items-center gap-2 px-2"
                                @click.prevent="handleDelete"
                            >
                                <DeleteIcon />
                                <span>Delete</span>
                            </button>
                        </li>
                    </ul>
                </el-popover>
            </div>
        </div>

        <div class="card-content">
            <div class="row">
                <div class="col-11">
                    <div v-if="editingComment !== comment.id" class="comment-text" v-html="renderedContext" ></div>
                    <input
                        v-else
                        ref="editInputRef"
                        v-model="editText"
                        type="text"
                        class="edit-input"
                        @keydown="handleKeydown($event, 'comment')"
                        @blur="handleBlur('comment')"
                    />
                </div>
                <!-- <div class="col-1">
                    <DeleteIcon v-if="userStore.user.roles.includes('Super Admin')" @click="deleteComment('comment',comment.id)" :style="{ cursor: 'pointer' }" />
                </div> -->
            </div>
            
            <slot name="preview"></slot>
        </div>
        <div class="add-reply" v-if="!isClosed && canTakeAction">
            <div class="input-wrap">
                <input 
                    v-model="form.reply"
                    type="text"
                    class="reply-input"
                    :placeholder="replyPlaceholder"
                    :disabled="isSubmitting"
                    @keyup.enter="submitReply"
                />
                <div v-if="v$?.reply?.$error" class="error-text">
                    <small
                        v-for="e in v$.reply.$errors"
                        :key="e.$uid"
                        class="text-danger"
                    >
                        {{ e.$message }}
                    </small>
                </div>
            </div>
            <button
                class="send-btn"
                :disabled="isSubmitting"
                @click="submitReply"
            >
                <SendCommentIcon />
                <!-- <img :src="sendIcon" alt="send" /> -->
            </button>
        </div>
        <div v-else class="closed-banner"><span v-if="canTakeAction">Comment closed</span></div>
        <div class="replies" v-if="localReplies.length">
            <div v-for="r in localReplies" :key="r.id" class="reply">
                <div class="card-header">
                    <div class="user-info">
                        <div class="reply-avatar">{{ getReplyInitial(r) }}</div>
                        <div class="user-details">
                            <strong class="user-name">{{
                                getReplyName(r)
                            }}</strong>
                            <small class="timestamp">{{
                                formatDate(r.created_at || r.ts)
                            }}</small>
                        </div>
                    </div>
                    <div class="actions-right"  v-if="!r.is_resolved">
                        <el-popover  v-if="canTakeAction"
                            placement="bottom-end"
                            :offset="4"
                            trigger="click"
                            popper-class="comment-popover"
                            :append-to-body="false"
                            v-model:visible="replyPopovers[r.id]"
                        >
                            <template #reference>
                                <el-button
                                    size="small"
                                    type="primary"
                                    style="background: none; border: none; padding: 0;"
                                >
                                    <img
                                        :src="upDotsIcon"
                                        style="width: 22px; height: 22px; cursor: pointer;"
                                        alt=""
                                    />
                                </el-button>
                            </template>

                            <ul class="list-unstyled dropdown-menu-end mb-0 p-0">
                                <li>
                                    <button
                                        class="dropdown-item d-flex align-items-center gap-3"
                                        @click.prevent="handleReplyEdit(r.id)"
                                    >
                                        <EditIcon />
                                        <span>Edit</span>
                                    </button>
                                </li>
                                <li>
                                    <button
                                        v-if="userStore.user.roles.includes('Super Admin')"
                                        class="dropdown-item d-flex align-items-center gap-3"
                                        @click.prevent="handleReplyDelete(r.id)"
                                    >
                                        <DeleteIcon />
                                        <span>Delete</span>
                                    </button>
                                </li>
                            </ul>
                        </el-popover>
                    </div>
                </div>
                <div class="card-content">
                    <div class="row">
                        <div class="col-11">
                            <div v-if="editingReply !== r.id" class="comment-text">{{ r.reply || r.text }}</div>
                            <input
                                v-else
                                :ref="el => { if (el) replyEditInputRefs[r.id] = el }"
                                v-model="editText"
                                type="text"
                                class="edit-input"
                                @keydown="handleKeydown($event, 'reply', r.id)"
                                @blur="handleBlur('reply', r.id)"
                            />
                        </div>
                        <!-- <div class="col-1">
                            <DeleteIcon v-if="userStore.user.roles.includes('Super Admin')" @click="deleteComment('reply',r.id)" :style="{ cursor: 'pointer' }" />
                        </div> -->
                    </div>
                    
                </div>
            </div>
        </div>
    </div>

    <!-- Delete Modal -->
    <DeleteModal
        class="delete-modal"
        :show="deleteModal[0]"
        @close="deleteModal[0] = false"
        :text="deleteItem.type"
        
        @save="confirmDelete"
    />
</template>



<style scoped>
.add-reply {
    display: flex;
    align-items: center;
    gap: 8px;
}
.input-wrap {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
}
.error-text {
    margin-top: 4px;
    line-height: 1.2;
}
.error-text .text-danger {
    font-size: 0.8rem;
    display: block;
}
.actions-right {
    display: flex;
    align-items: center;
    gap: 8px;
}


.comment-popover .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    text-align: left;
    transition: background-color 0.2s;
    overflow: visible;
}

.comment-popover .dropdown-item:hover {
    background-color: #f9fafb;
}

.comment-popover .dropdown-item svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    display: block;
    overflow: visible;
    box-sizing: content-box;
}

.comment-popover .dropdown-item span {
    flex: 1;
}

.edit-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
    color: #374151;
    background: #fff;
    outline: none;
}

.edit-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.reply .actions-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

</style>
